import type { ModuleSnapshot } from '@ctcells/contract'
import { enumLabel, t } from '@ctcells/i18n'

import type {
  ShakerFigureState,
  ShakerLatchShape,
  ShakerStop,
} from '../../figures/ModuleFigures.tsx'

/**
 * 셰이커 그림이 받는 값 — 🔴 **응답에 «있는» 값만 읽는다. 화면이 상태를 짓지 않는다.**
 *
 * | 그림 | 응답 칸 | 모르면 |
 * | --- | --- | --- |
 * | 슬라이더 자리 | `telemetry.slider_position` | 플랫폼을 세 자리 어디에도 세우지 않는다(점선 윤곽) |
 * | 셰이커 래치 | `shaker_latch.state`(6상태) | 걸쇠 축만 서고 턱이 없다 |
 * | 트레이 | `tray_latch.state`(`engaged`·`released`·`unknown`) | 판 위에 아무것도 안 그린다 |
 * | 쉐이킹 | `state === 'SHAKING'`(모듈 상태머신 · `shake` → `SHAKING`) | 진동선 없음 |
 *
 * ⚠ `slider_position` 은 `telemetry` 자유형이라 **계약이 값 어휘를 정하지 않았다.** 받는 어휘는
 *   capability `tray_transport.positions`(`rear` · `work` · `front` — module-core `profiles/staining.yaml`)
 *   셋뿐이고, 그 밖의 값은 「모름」으로 둔다. 2026-09-29 확인: module-core 가 `telemetry` ·
 *   `shaker_latch` · `tray_latch` 를 **채우는 자리가 없다**(`app/api/routes.py` — 셋 다 `null` 로 나간다).
 *   ⇒ live 에서는 넷 다 「모름」이 정답이다. 목도 서버 모양 그대로 `null` 이다.
 * 🔴 `idle_unknown` · `unknown` 을 「닫힘」으로 접지 않는다(`FR-LH-19` — 회전 개시 조건이 `idle_closed` 한 값).
 * 🔴 `INCUBATING` 은 쉐이킹이 아니다 — 같은 셰이커 블록이 켜지지만 진동선은 `SHAKING` 에서만 선다.
 */
const STOPS: Readonly<Record<string, ShakerStop>> = { rear: 'rear', work: 'work', front: 'front' }

const LATCH_SHAPES: Readonly<Record<string, ShakerLatchShape>> = {
  idle_open: 'open',
  idle_closed: 'closed',
  opening: 'moving',
  closing: 'moving',
}

const strOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null

export interface ShakerView extends ShakerFigureState {
  /** 그림 아래 한 줄 */
  statusLabel: string
  /** 후문(자동 · 6축 쪽) — `interlock.doors[]` 의 `rear` 면 `state`. 면이 없으면 `null`(「닫힘」으로 접지 않는다) */
  door: 'open' | 'closed' | null
  /** 후문 글자 — 「후문 닫힘」 · 「후문 열림」 · 「후문 미발행」 */
  doorLabel: string
}

/** 후문 상태 — 계약 `interlock.doors[] {face, state}`. 모양이 다르거나 `rear` 면이 없으면 `null` */
export function rearDoor(snapshot: ModuleSnapshot | undefined): 'open' | 'closed' | null {
  const doors: unknown = snapshot?.interlock?.doors
  if (!Array.isArray(doors)) return null
  for (const d of doors as unknown[]) {
    if (typeof d !== 'object' || d === null) continue
    const { face, state } = d as { face?: unknown; state?: unknown }
    if (face === 'rear' && (state === 'open' || state === 'closed')) return state
  }
  return null
}

export function shakerView(snapshot: ModuleSnapshot | undefined): ShakerView {
  const stopRaw = strOrNull(snapshot?.telemetry?.slider_position)
  const latchRaw = strOrNull(snapshot?.shaker_latch?.state)
  const trayRaw = strOrNull(snapshot?.tray_latch?.state)
  const shaking = snapshot?.state === 'SHAKING'

  const parts: string[] = []
  if (shaking) parts.push(enumLabel('enum.moduleState', 'SHAKING'))
  if (latchRaw !== null) {
    parts.push(t('monitoring.shakerLatch', { state: enumLabel('enum.shakerLatch', latchRaw) }))
  }

  const door = rearDoor(snapshot)
  return {
    stop: stopRaw === null ? null : (STOPS[stopRaw] ?? null),
    latch: latchRaw === null ? null : (LATCH_SHAPES[latchRaw] ?? null),
    tray: trayRaw === 'engaged' ? 'present' : trayRaw === 'released' ? 'absent' : null,
    shaking,
    statusLabel: parts.length === 0 ? t('value.notPublished') : parts.join(' · '),
    door,
    doorLabel: t('monitoring.shaker.door', {
      state: t(
        door === 'open'
          ? 'monitoring.shaker.door.open'
          : door === 'closed'
            ? 'monitoring.shaker.door.closed'
            : 'monitoring.shaker.door.unknown',
      ),
    }),
  }
}
